/**
 * Build-time gate for the Layer 2 Deep Dive UI.
 *
 * Trevor, PR #504 review 2026-09-08: the frontend used to carry an
 * always-true constant, which meant the button rendered in every
 * environment and relied on the backend 503 to hide a feature that was not
 * deployed there. The value now comes from `VITE_DEEP_DIVE_ENABLED`, set per
 * build: true in the W9 preview, absent (so false) everywhere the backend
 * has no `DEEP_DIVE_ENABLED`.
 *
 * The backend `DEEP_DIVE_ENABLED` env var remains the enforcement boundary —
 * this flag only decides whether the UI offers the affordance. A user who
 * forges a request against a disabled backend still gets a 503.
 *
 * `__DEEP_DIVE_ENABLED__` is substituted by `vite.config.js` from that env
 * var; see the comment there for why the read is not a direct
 * `import.meta.env` lookup. Jest does not run through Vite and never
 * defines it, so the `typeof` guard below fails closed.
 */

/** Truthy spellings accepted from the env var. Mirrors the backend's
 * `is_deep_dive_enabled()` in `services/deep_dive_service.py`. */
const TRUTHY = new Set(['true', '1', 'yes', 'on']);

/**
 * Normalize a raw flag value to a boolean. Exported so the parsing contract
 * is testable without a build.
 *
 * @param {unknown} raw Value as substituted at build time.
 * @returns {boolean} Whether the Deep Dive UI should be offered.
 */
export function resolveDeepDiveEnabled(raw) {
    if (typeof raw === 'boolean') return raw;
    return TRUTHY.has(String(raw ?? '').trim().toLowerCase());
}

function readBuildTimeFlag() {
    // eslint-disable-next-line no-undef
    return typeof __DEEP_DIVE_ENABLED__ === 'undefined' ? '' : __DEEP_DIVE_ENABLED__;
}

export const DEEP_DIVE_ENABLED = resolveDeepDiveEnabled(readBuildTimeFlag());

export default DEEP_DIVE_ENABLED;
